import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ExternalLink, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { arbitrageOpportunities } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Ambas as origens (API viva e mock) são reduzidas a esta forma para que a
// tabela tenha um único caminho de render. Campos ausentes ficam undefined e
// o painel expandido simplesmente não os mostra.
const normalize = (opp) => ({
  id: opp.id,
  marketPair: opp.marketPair || `${opp.platform1}//${opp.platform2}`,
  title: opp.title,
  volume: opp.volume,
  spread: opp.spread,
  signal: opp.signal,
  platform1: opp.platform1,
  platform2: opp.platform2,
  bestBid: opp.best_bid,
  bestAsk: opp.best_ask,
  midPrice: opp.mid_price,
  url: opp.url,
});

const fallbackRows = arbitrageOpportunities.slice(0, 10).map(normalize);

const SignalBadge = ({ signal }) => {
  const direction = signal?.direction || 'NEUTRAL';
  const confidence = signal?.confidence;
  const colors = direction === 'BUY'
    ? 'bg-green-500/20 text-green-400'
    : direction === 'SELL'
    ? 'bg-red-500/20 text-red-400'
    : 'bg-gray-500/20 text-gray-400';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${colors}`}>
      {direction}{typeof confidence === 'number' ? ` ${(confidence * 100).toFixed(0)}%` : ''}
    </span>
  );
};

const MarketDepthSection = () => {
  const [rows, setRows] = useState(fallbackRows);
  const [isLive, setIsLive] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Dados vivos do mesmo endpoint que o Terminal usa. Se a API não responder,
  // a landing fica no mock — marcado como SAMPLE DATA, nunca como vivo.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/markets/opportunities`, { params: { limit: 10 } })
      .then(({ data }) => {
        if (cancelled) return;
        const live = (data.opportunities || []).map(normalize).filter(r => r.title);
        if (live.length > 0) {
          setRows(live);
          setIsLive(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const topSpread = Math.max(...rows.map(r => (typeof r.spread === 'number' ? r.spread : 0)));

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <section id="market-depth" className="relative py-20 bg-[#06070d]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-4xl font-bold text-white mb-2">Market Depth</h2>
              <div className="flex items-center space-x-4">
                <span className="text-[#f2e2a6] text-lg font-semibold">TOP SPREAD: {topSpread.toFixed(2)}%</span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-400">Polymarket / Opinion</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isLive && <div className="w-2 h-2 bg-[#f2e2a6] rounded-full animate-pulse" />}
              <span className={`text-sm font-mono uppercase ${isLive ? 'text-[#f2e2a6]' : 'text-gray-400'}`}>
                {isLive ? 'LIVE DATA' : 'SAMPLE DATA'}
              </span>
            </div>
          </div>

          <div className="inline-flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
            <TrendingUp className="w-4 h-4 text-[#f2e2a6]" />
            <span className="text-white font-mono text-sm">MARKET OPPORTUNITIES</span>
          </div>
        </div>

        {/* Market table */}
        <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          {/* Telemóvel: cartões em vez de tabela de 5 colunas espremida. */}
          <div className="md:hidden divide-y divide-white/5">
            {rows.map((opp, index) => (
              <div key={`card-${opp.id}-${index}`} className="p-4">
                <div className="text-gray-400 text-xs mb-1">{opp.marketPair}</div>
                <div className="text-white font-medium mb-3">{opp.title}</div>
                <div className="flex items-center justify-between text-sm">
                  <SignalBadge signal={opp.signal} />
                  <span className="text-[#f2e2a6] font-semibold">
                    {typeof opp.spread === 'number' ? `${opp.spread.toFixed(2)}%` : '—'}
                  </span>
                  <span className="text-gray-400">{opp.volume}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Market Pair</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Volume</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Spread</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Signal</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Analysis</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((opp, index) => (
                  <React.Fragment key={`${opp.id}-${index}`}>
                    <tr
                      className="border-b border-white/5 hover:bg-white/5 transition-all cursor-pointer"
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="py-4 px-6">
                        <div className="text-gray-400 text-xs mb-1">{opp.marketPair}</div>
                        <div className="text-white text-sm font-medium max-w-md">{opp.title}</div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white font-semibold">{opp.volume}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-[#f2e2a6] font-semibold">
                          {typeof opp.spread === 'number' ? `${opp.spread.toFixed(2)}%` : '—'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <SignalBadge signal={opp.signal} />
                      </td>
                      <td className="py-4 px-6">
                        <Button
                          size="sm"
                          onClick={() => toggleExpand(index)}
                          className={`transition-all flex items-center space-x-1 ${
                            hoveredRow === index || expandedRow === index
                              ? 'bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90'
                              : 'bg-white/10 text-white hover:bg-white/20'
                          }`}
                        >
                          <span>{expandedRow === index ? 'Close' : 'Analyze'}</span>
                          {expandedRow === index ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </Button>
                      </td>
                    </tr>
                    {expandedRow === index && (
                      <tr className="bg-white/5 border-b border-white/5">
                        <td colSpan="5" className="py-6 px-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <h4 className="text-white font-semibold text-lg mb-4">Signal Analysis</h4>
                              <div className="bg-black/40 border border-white/10 rounded-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-400 text-xs">Direction</span>
                                  <SignalBadge signal={opp.signal} />
                                </div>
                                {(opp.signal?.reasons || []).map((reason, i) => (
                                  <div key={i} className="flex items-center space-x-2">
                                    <div className="w-1.5 h-1.5 bg-[#f2e2a6] rounded-full" />
                                    <span className="text-white text-sm">{reason}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="space-y-4">
                              <h4 className="text-white font-semibold text-lg mb-4">Market Data</h4>
                              <div className="grid grid-cols-2 gap-4">
                                {[
                                  ['Best Bid', opp.bestBid],
                                  ['Best Ask', opp.bestAsk],
                                  ['Mid Price', opp.midPrice],
                                ].filter(([, value]) => typeof value === 'number').map(([label, value]) => (
                                  <div key={label} className="bg-black/40 border border-white/10 rounded-lg p-4">
                                    <div className="text-gray-400 text-xs mb-1">{label}</div>
                                    <div className="text-white font-bold">${value.toFixed(3)}</div>
                                  </div>
                                ))}
                                <div className="bg-black/40 border border-white/10 rounded-lg p-4">
                                  <div className="text-gray-400 text-xs mb-1">Volume</div>
                                  <div className="text-white font-bold">{opp.volume}</div>
                                </div>
                              </div>
                              <div className="flex space-x-2 mt-4">
                                <Button asChild className="flex-1 bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90">
                                  <a
                                    href={opp.url || 'https://polymarket.com'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Trade on {opp.platform1 || 'Polymarket'}
                                  </a>
                                </Button>
                              </div>
                              <div className="flex space-x-2 mt-2">
                                <Button asChild className="flex-1 bg-white/10 text-white hover:bg-white/20">
                                  <a
                                    href={`https://opinion.trade`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Trade on {opp.platform2 || 'Opinion'}
                                  </a>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* More opportunities CTA */}
          <div className="border-t border-white/10 bg-black/40 px-6 py-8 text-center">
            <div className="text-gray-400 text-lg mb-4">VIEW MORE IN TERMINAL</div>
            <a href="/terminal">
              <Button className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold px-8 transition-all hover:scale-105">
                Launch Terminal
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketDepthSection;
